import { useMemo } from "react";
import { Grid, Typography } from "@mui/material";
import { useField } from "formik";
import moment from "moment";

import { getFormatDate } from "@/helpers/utils";
import { PRINT_TYPE } from "@/features/campaigns/direct-mail/rrd/utils/rrdConstants";

type LocationHeaderDetailsProps = {
  printType?: string;
  mailingDateFieldName: string;
  frontCouponExpirationDateFieldName: string;
  backCouponExpirationDateFieldName: string;
};

function LocationHeaderDetails({
  printType,
  mailingDateFieldName,
  frontCouponExpirationDateFieldName,
  backCouponExpirationDateFieldName,
}: LocationHeaderDetailsProps) {
  const [mailingDateField] = useField<string>(mailingDateFieldName);
  const [frontCouponExpField] = useField<string>(
    frontCouponExpirationDateFieldName
  );
  const [backCouponExpField] = useField<string>(
    backCouponExpirationDateFieldName
  );

  const isPrintTypeValue = useMemo(() => {
    return printType === PRINT_TYPE.VALUE.value;
  }, [printType]);

  const mailingWeeks = useMemo(() => {
    if (!mailingDateField?.value) {
      return "";
    }
    const startDate = moment(mailingDateField?.value);
    if (!startDate.isValid()) {
      return "";
    }
    const endDate = startDate.clone().endOf("week");

    return `${getFormatDate(startDate, "MMM Do, YYYY")} - ${getFormatDate(endDate, "MMM Do, YYYY")}`;
  }, [mailingDateField.value]);

  const frontExpDate = useMemo(() => {
    if (
      frontCouponExpField?.value &&
      moment(frontCouponExpField.value)?.isValid()
    ) {
      return frontCouponExpField.value;
    }
    return "";
  }, [frontCouponExpField.value]);

  const backExpDate = useMemo(() => {
    if (
      backCouponExpField?.value &&
      moment(backCouponExpField.value)?.isValid()
    ) {
      return backCouponExpField.value;
    }
    return "";
  }, [backCouponExpField.value]);

  if (!mailingWeeks && !frontExpDate) {
    return null;
  }

  return (
    <Grid sx={{ ml: 4, mb: 0.5 }} container rowSpacing={0} columnSpacing={1}>
      {mailingWeeks && (
        <Grid size={{ xs: 12, sm: "auto" }}>
          <Typography color="text.primary" fontWeight={600} variant="caption">
            Mailing Date:
          </Typography>{" "}
          <Typography variant="caption" fontWeight={600}>
            {mailingWeeks}
          </Typography>{" "}
        </Grid>
      )}
      {frontExpDate ? (
        <Grid size={{ xs: 12, sm: "auto" }}>
          <Typography color="text.primary" fontWeight={600} variant="caption">
            Coupon Expiration Date:
          </Typography>{" "}
          <Typography variant="caption" fontWeight={600}>
            {getFormatDate(frontExpDate, "MMM Do, YYYY")}

            {isPrintTypeValue && backExpDate
              ? `, ${getFormatDate(backExpDate, "MMM Do, YYYY")}`
              : null}
          </Typography>
        </Grid>
      ) : null}
    </Grid>
  );
}

export default LocationHeaderDetails;
